import React, { useMemo } from 'react'
import Head from 'next/head'

import useAugmentedRouter from 'hooks/useAugmentedRouter'

import type { CombinedListing } from './staticData'

import type {
  AlgoliaListing,
  BoomiListing,
  BreadcrumbListItem,
  Review,
  YoastMetadata,
} from 'types/externalData'
import type { DetailedLocationData } from 'types/locationLanding'

type Listing = Partial<
  AlgoliaListing &
    Pick<BoomiListing, Exclude<keyof BoomiListing, keyof AlgoliaListing>>
>

type Location = {
  address: string
  city: string
  country: string
  country_short: string
  lat: number
  lng: number
  place_id: string
  post_code: string
  state: string
  state_short: string
  zoom: number
}

type FaqData = { question: string; answer: string }[]

export const buildDescription = (listing) => {
  if (listing && 'adContent' in listing && listing['adContent'].length) {
    const description = listing['adContent'].filter(
      ({ type }: BoomiListing['adContent'][0]) => type === 'summary',
    )
    if (description) {
      return description[0]['text']
    }
  }

  return ''
}

export const buildMetaDescription = (listing: CombinedListing) => {
  let description = ''

  if (listing && 'adContent' in listing && listing['adContent'].length) {
    description = `Rent this vacation home in ${listing.City}, ${
      listing.State
    } – Sleeps ${listing['Max Occupancy']} guests • ${
      listing.Bedrooms
    } Bedrooms • ${listing.Bathrooms} Bathrooms • $${
      listing['Average Per Night']
    } avg/night • Read ${listing['Number of Reviews']} reviews and view${
      listing.images?.length ? ` ${listing.images.length} ` : ' '
    }photos!`

    const firstSentence = listing['adContent']
      .filter(({ type }: BoomiListing['adContent'][0]) => type === 'summary')[0]
      .text.match(/(.*?)[.?!]/)
      ? listing['adContent']
          .filter(
            ({ type }: BoomiListing['adContent'][0]) => type === 'summary',
          )[0]
          .text.match(/(.*?)[.?!]/)[0]
      : ''

    description += ` ${firstSentence}`
  }

  return description
}

const buildBestRating = (reviews: Review[]) => {
  let best = 0

  if (!reviews || !reviews.length) {
    return ''
  }

  for (let i = 0; i < reviews.length; i++) {
    const review = reviews[i]

    if ('rating' in review && +review['rating'] > best) {
      best = +review['rating']
    }
  }

  return best
}

const buildWorstRating = (reviews: Review[]) => {
  let worst = 5

  if (!reviews || !reviews.length) {
    return ''
  }

  for (let i = 0; i < reviews.length; i++) {
    const review = reviews[i]

    if ('rating' in review && +review['rating'] < worst) {
      worst = +review['rating']
    }
  }

  return worst
}

const buildSeoImage = (listing: CombinedListing) => {
  if ('ImageUrl' in listing) {
    return `https://res.cloudinary.com/evolve-vacation-rental-network/image/upload/ar_16:9,c_fill,dpr_2.0,f_auto,q_auto,w_320/v1/${listing['ImageUrl']}`
  } else if (
    'units' in listing &&
    listing['units'].length &&
    'images' in listing['units'][0] &&
    listing['units'][0]['images'].length
  ) {
    return listing['units'][0]['images'][0]['url']
  }

  return ''
}

const determinePetsAllowed = (listing: CombinedListing) => {
  if (
    listing &&
    'units' in listing &&
    listing['units'].length &&
    'amenities' in listing['units'][0]
  ) {
    return listing['units'][0]['amenities'].some(
      (amenity: BoomiListing['units'][0]['amenities'][0]) =>
        amenity.name.toLowerCase() === 'pets allowed',
    )
  }

  return false
}

const buildReviewCount = (listing: CombinedListing) => {
  if (
    listing['Number of Reviews'] ||
    (listing['Number of Reviews'] && +listing['Number of Reviews'] === 0)
  ) {
    return listing['Number of Reviews']
  } else if (
    listing['number of reviews'] ||
    +listing['number of reviews'] === 0
  ) {
    return listing['number of reviews']
  }

  return 0
}

const buildAggregateReview = (listing: CombinedListing, reviews: Review[]) => {
  if (listing['number of reviews'] && +listing['number of reviews'] > 0) {
    return {
      aggregateRating: {
        '@type': 'AggregateRating',
        'itemReviewed': 'listing',
        'ratingValue': listing['Average Rating'],
        'reviewCount': buildReviewCount(listing),
        'bestRating': buildBestRating(reviews),
        'worstRating': buildWorstRating(reviews),
      },
    }
  }

  return ''
}

const buildAddress = (regionList: string[], localityList: string[]) => ({
  '@type': 'PostalAddress',
  'addressRegion': regionList[0] ?? regionList[1],
  'addressLocality': localityList[0] ?? localityList[1],
})

const getTopAmenities = (
  amenityList: BoomiListing['units'][0]['amenities'],
) => {
  const sortOrder: { [key: string]: number } = {
    'Pool': 0,
    'Private Hot Tub': 1,
    'Beachfront': 2,
    'Oceanfront': 3,
    'Wireless Internet (WIFI)': 4,
    'Air-Conditioning': 5,
    'Community Pool': 6,
    'Kitchen': 7,
    'Parking': 8,
  }

  const filteredAmenities: BoomiListing['units'][0]['amenities'] = []

  for (let i = 0; i < amenityList.length; i++) {
    for (let j = 0; j < Object.keys(sortOrder).length; j++) {
      if (Object.keys(sortOrder)[j] === amenityList[i].name) {
        filteredAmenities.push(amenityList[i])
      }
    }
    // Count Private Pool, Pirvate Indoor Pool, Private Outdoor Pool as 'Pool'
    if (amenityList[i].name.match(/Private( [^\s]+ |\s)Pool/gi)) {
      filteredAmenities.push({ name: 'Pool', category: 'Pool Amenities' })
    }
  }

  return filteredAmenities.sort((a, b) => sortOrder[a.name] - sortOrder[b.name])
}

const buildAmenityFeatures = (
  amenityList: BoomiListing['units'][0]['amenities'],
) => {
  return getTopAmenities(amenityList).map((amenity) => ({
    '@type': 'LocationFeatureSpecification',
    'name': amenity.name,
    'value': 'TRUE',
  }))
}

const StructuredDataComponent = ({
  data,
  id,
}: {
  data: { [key: string]: any }
  id: string
}) => (
  <Head>
    <script
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
      id={id}
      type="application/ld+json"
    />
  </Head>
)

export const BreadcrumbsStructuredData = ({
  elements,
}: {
  elements: BreadcrumbListItem[]
}) => {
  const itemListElements: BreadcrumbListItem[] = useMemo(() => {
    return elements.map((element, index) => ({
      '@type': 'ListItem',
      'position': index + 1,
      ...element,
    }))
  }, [elements])

  const data = useMemo(() => {
    if (itemListElements.length === 0) {
      return null
    }

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'name': itemListElements[itemListElements.length - 1].name,
      'itemListElement': itemListElements,
    }
  }, [itemListElements])

  if (!data) {
    return null
  }

  return <StructuredDataComponent data={data} id="sd-breadcrumbs" />
}

export const ListingStructuredData = ({
  listing,
  reviews,
}: {
  listing: CombinedListing
  reviews: Review[]
}) => {
  const amenityFeatures = useMemo(
    () => buildAmenityFeatures(listing.units[0].amenities),
    [listing.units],
  )
  const data = useMemo(
    () => ({
      '@context': 'https://schema.org/',
      '@type': 'LodgingBusiness',
      '@id': listing.objectID,
      'name': listing.Headline,
      'image': [buildSeoImage(listing)],
      'description': buildDescription(listing),
      'petsAllowed': determinePetsAllowed(listing) === true ? 'TRUE' : 'FALSE',
      'address': buildAddress(
        [
          listing.units[0].addresses[0].stateShort,
          listing.units[0].addresses[0].countryShort,
        ],
        [
          listing.units[0].addresses[0].locality,
          listing.units[0].addresses[0].state,
        ],
      ),
      'amenityFeature':
        amenityFeatures.length > 3
          ? amenityFeatures.slice(0, 3)
          : amenityFeatures,
      'numberOfRooms': listing.units[0].configuration.bedrooms,
      'checkinTime': listing['Check-in Time'],
      'checkoutTime': listing['Check-out Time'],
      'brand': {
        '@type': 'Brand',
        'name': 'Evolve Vacation Rental',
      },
      ...('Number of Reviews' in listing || 'number of reviews' in listing
        ? buildAggregateReview(listing, reviews)
        : ''),
    }),
    [amenityFeatures, listing, reviews],
  )

  return <StructuredDataComponent data={data} id="sd-listing" />
}

export const FaqStructuredData = ({ faqData }: { faqData: FaqData }) => (
  <StructuredDataComponent
    data={{
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      'mainEntity': faqData.map((faq: FaqData[0]) => ({
        '@type': 'Question',
        'name': faq.question,
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': faq.answer,
        },
      })),
    }}
    id="sd-faq"
  />
)

export const WebpageStructuredData = ({
  yoastData,
  canonicalUrl,
  name,
  description,
}: {
  yoastData: YoastMetadata
  canonicalUrl?: string
  name?: string
  description?: string
}) => {
  const router = useAugmentedRouter()
  return (
    <StructuredDataComponent
      data={{
        '@context': 'https://schema.org/',
        '@type': 'Webpage',
        'name': yoastData.yoast_wpseo_title
          ? yoastData.yoast_wpseo_title
          : name,
        'description': yoastData.yoast_wpseo_metadesc
          ? yoastData.yoast_wpseo_metadesc
          : description,
        'url': canonicalUrl
          ? canonicalUrl
          : process.env.NEXT_PUBLIC_ORIGIN + router.asPath.split('?')[0],
      }}
      id="sd-webpage"
    />
  )
}

export const CollectionPageStructuredData = ({
  name,
  description,
  about,
  language,
  image,
}: {
  name: string
  description: string
  about: string
  language: string
  image: string
}) => {
  const router = useAugmentedRouter()
  return (
    <StructuredDataComponent
      data={{
        '@context': 'https://schema.org/',
        '@type': 'CollectionPage',
        'name': name,
        'description': description,
        'about': about,
        'inLanguage': language,
        'url': process.env.NEXT_PUBLIC_ORIGIN + router.asPath.split('?')[0],
        'primaryImageOfPage': image,
      }}
      id="sd-collection"
    />
  )
}

export const ArticleStructuredData = ({ article, url }) => (
  <StructuredDataComponent
    data={{
      '@context': 'https://schema.org/',
      '@type': 'BlogPosting',
      'mainEntityOfPage': {
        '@type': 'WebPage',
        '@id': url,
      },
      'headline': article.yoast_meta.yoast_wpseo_title
        ? article.yoast_meta.yoast_wpseo_title
        : article.title.rendered,
      'image': article.featured_image_url,
      'author': {
        '@type': 'Person',
        'name': article.author_details.nickname,
      },
      'publisher': {
        '@type': 'Organization',
        'name': 'Evolve Vacation Rental',
        'logo': {
          '@type': 'ImageObject',
          'url': `${process.env.NEXT_PUBLIC_ORIGIN}/assets/logos/evolve-logo.png`,
          'height': '48px',
          'width': '240px',
        },
      },
      'description': article.yoast_meta.yoast_wpseo_metadesc
        ? article.yoast_meta.yoast_wpseo_metadesc
        : article.excerpt.rendered,
      'datePublished': article.date,
      'dateModified': article.modified,
      'inLanguage': 'en-US',
    }}
    id="sd-article"
  />
)

export const OrganizationStructuredData = () => (
  <StructuredDataComponent
    data={{
      '@context': 'https://schema.org/',
      '@type': 'Organization',
      'name': 'Evolve Vacation Rental',
      'description': 'Vacation Rentals Made Easy',
      'url': process.env.NEXT_PUBLIC_ORIGIN,
      'logo': {
        '@type': 'ImageObject',
        'url': `${process.env.NEXT_PUBLIC_ORIGIN}/assets/logos/evolve-logo.png`,
      },
    }}
    id="sd-organization"
  />
)

export const LocationPagePlaceStructuredData = ({
  location,
}: {
  location: Location
}) => (
  <StructuredDataComponent
    data={{
      '@context': 'https://schema.org/',
      '@type': 'Place',
      'name': location.address.split(',')[0] + ' Vacation Rentals',
      'address': buildAddress(
        [location.state_short, location.country_short],
        [location.address.split(',')[0], location.state],
      ),
    }}
    id="sd-place"
  />
)

export const AutoLocationPagePlaceStructuredData = ({
  cityName,
  slugArr,
  stateData,
}: {
  cityName: string
  slugArr: string[]
  stateData: DetailedLocationData
}) => {
  const upperCaseSlugArr = useMemo(
    () => slugArr.map((el) => el.toUpperCase()),
    [slugArr],
  )
  return (
    <StructuredDataComponent
      data={{
        '@context': 'https://schema.org/',
        '@type': 'Place',
        'name': `${cityName} Vacation Rentals`,
        'address': buildAddress(
          [upperCaseSlugArr[1], upperCaseSlugArr[0]],
          [cityName, stateData?.name ?? upperCaseSlugArr[1]],
        ),
      }}
      id="sd-auto-place"
    />
  )
}

export const DateModifiedStructuredData = ({ date }: { date: Date }) => (
  <StructuredDataComponent
    data={{
      '@context': 'https://schema.org',
      '@type': 'ItemPage',
      'dateModified': date,
    }}
    id="sd-date-modified"
  />
)

/**
 * Extracts price range from listing's PricePerDay data
 * Returns min/max prices or falls back to average
 */
const getPriceRange = (listing: CombinedListing) => {
  const pricePerDay = listing.PricePerDay || {}
  const prices = Object.values(pricePerDay).filter(
    (p): p is number => typeof p === 'number' && p > 0,
  )

  if (prices.length === 0) {
    const avgPrice = listing['Average Per Night'] || 0
    return { lowPrice: avgPrice, highPrice: avgPrice }
  }

  return {
    lowPrice: Math.min(...prices),
    highPrice: Math.max(...prices),
  }
}

/**
 * Availability codes that indicate bookable dates
 * DC = Available, DO = Stay/Checkout, DI = Stay/Checkin, DX = Stay Only
 */
const BOOKABLE_AVAILABILITY_CODES = ['DC', 'DO', 'DI', 'DX']

/**
 * Checks if listing has any available dates
 */
const hasAvailableDates = (listing: CombinedListing): boolean => {
  const availability = listing.Availability || {}
  return Object.values(availability).some(
    (status) =>
      typeof status === 'string' &&
      BOOKABLE_AVAILABILITY_CODES.includes(status),
  )
}

/**
 * Product/Offer Schema for listing pages
 * Enables rich snippets with pricing information in AI search results
 */
export const ProductStructuredData = ({
  listing,
  canonicalUrl,
}: {
  listing: CombinedListing
  canonicalUrl: string
}) => {
  const hasReviews =
    listing['Number of Reviews'] && +listing['Number of Reviews'] > 0

  const { lowPrice, highPrice } = getPriceRange(listing)
  const isAvailable = hasAvailableDates(listing)

  return (
    <StructuredDataComponent
      data={{
        '@context': 'https://schema.org',
        '@type': 'Product',
        'name': listing.Headline,
        'description': buildDescription(listing),
        'image': buildSeoImage(listing),
        'url': canonicalUrl,
        'brand': {
          '@type': 'Brand',
          'name': 'Evolve Vacation Rental',
        },
        'category': 'Vacation Rental',
        ...(hasReviews && {
          aggregateRating: {
            '@type': 'AggregateRating',
            'ratingValue': listing['Average Rating'],
            'reviewCount': listing['Number of Reviews'],
            'bestRating': 5,
            'worstRating': 1,
          },
        }),
        'offers': {
          '@type': 'AggregateOffer',
          'priceCurrency': 'USD',
          'lowPrice': lowPrice,
          'highPrice': highPrice,
          'offerCount': 1,
          'availability': isAvailable
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          'priceValidUntil': new Date(
            Date.now() + 30 * 24 * 60 * 60 * 1000,
          ).toISOString(),
          'url': canonicalUrl,
        },
      }}
      id="sd-product"
    />
  )
}

/**
 * SearchAction Schema for homepage
 * Enables sitelinks searchbox in Google and AI-powered search results
 */
export const SearchActionStructuredData = () => (
  <StructuredDataComponent
    data={{
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      'name': 'Evolve Vacation Rental',
      'url': process.env.NEXT_PUBLIC_ORIGIN,
      'potentialAction': {
        '@type': 'SearchAction',
        'target': {
          '@type': 'EntryPoint',
          'urlTemplate': `${process.env.NEXT_PUBLIC_ORIGIN}/vacation-rentals/search?query={search_term_string}`,
        },
        'query-input': 'required name=search_term_string',
      },
    }}
    id="sd-search-action"
  />
)

/**
 * Individual Review Schema for listing pages
 * Structures individual guest reviews for AI extraction
 */
export const ReviewsStructuredData = ({
  reviews,
  listing,
}: {
  reviews: Review[]
  listing: CombinedListing
}) => {
  if (!reviews || reviews.length === 0) return null

  // Limit to first 10 reviews to avoid excessive markup
  const limitedReviews = reviews.slice(0, 10)

  return (
    <StructuredDataComponent
      data={{
        '@context': 'https://schema.org',
        '@type': 'Product',
        'name': listing.Headline,
        'review': limitedReviews.map((review) => ({
          '@type': 'Review',
          'reviewRating': {
            '@type': 'Rating',
            'ratingValue': review.rating,
            'bestRating': '5',
            'worstRating': '1',
          },
          'author': {
            '@type': 'Person',
            'name': review.reviewedBy || 'Guest',
          },
          'datePublished': review.createdDt,
          'reviewBody': review.reviewDetail || review.reviewSummary,
          ...(review.reviewResponse && {
            comment: {
              '@type': 'Comment',
              'text': review.reviewResponse,
              'author': {
                '@type': 'Organization',
                'name': 'Evolve Vacation Rental',
              },
            },
          }),
        })),
      }}
      id="sd-reviews"
    />
  )
}

/**
 * LocalBusiness Schema for location landing pages
 * Enhances location page discoverability for "vacation rentals in [city]" queries
 */
export const LocalBusinessStructuredData = ({
  cityName,
  stateName,
  stateShort,
  countryShort,
  listingCount,
  canonicalUrl,
}: {
  cityName: string
  stateName: string
  stateShort: string
  countryShort: string
  listingCount: number
  canonicalUrl: string
}) => (
  <StructuredDataComponent
    data={{
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': canonicalUrl,
      'name': `Evolve Vacation Rentals in ${cityName}`,
      'description': `Browse ${listingCount} vacation rentals in ${cityName}, ${stateName}. Book directly with Evolve for the best rates and 24/7 support.`,
      'url': canonicalUrl,
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': cityName,
        'addressRegion': stateShort || stateName,
        'addressCountry': countryShort,
      },
      'brand': {
        '@type': 'Brand',
        'name': 'Evolve Vacation Rental',
      },
      'parentOrganization': {
        '@type': 'Organization',
        'name': 'Evolve Vacation Rental',
        'url': process.env.NEXT_PUBLIC_ORIGIN,
      },
      'numberOfEmployees': {
        '@type': 'QuantitativeValue',
        'value': listingCount,
        'unitText': 'vacation rentals',
      },
    }}
    id="sd-local-business"
  />
)

/**
 * Enhanced Organization Schema with social profiles
 * Establishes brand entity for AI knowledge graphs
 */
export const EnhancedOrganizationStructuredData = () => (
  <StructuredDataComponent
    data={{
      '@context': 'https://schema.org',
      '@type': 'Organization',
      'name': 'Evolve Vacation Rental',
      'alternateName': 'Evolve',
      'description':
        'Evolve is a vacation rental management company helping property owners list, manage, and grow their rental business while providing guests with exceptional vacation experiences.',
      'url': process.env.NEXT_PUBLIC_ORIGIN,
      'logo': {
        '@type': 'ImageObject',
        'url': `${process.env.NEXT_PUBLIC_ORIGIN}/assets/logos/evolve-logo.png`,
        'width': '240',
        'height': '48',
      },
      'image': `${process.env.NEXT_PUBLIC_ORIGIN}/assets/logos/evolve-logo.png`,
      'sameAs': [
        'https://www.facebook.com/EvolveVacationRental',
        'https://twitter.com/EvolveVR',
        'https://www.instagram.com/evolvevacationrental',
        'https://www.linkedin.com/company/evolve-vacation-rental',
        'https://www.youtube.com/c/EvolveVacationRental',
        'https://www.pinterest.com/evolvevr/',
      ],
      'foundingDate': '2011',
      'foundingLocation': {
        '@type': 'Place',
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': 'Denver',
          'addressRegion': 'CO',
          'addressCountry': 'US',
        },
      },
      'contactPoint': [
        {
          '@type': 'ContactPoint',
          'telephone': '+1-877-818-1014',
          'contactType': 'customer service',
          'availableLanguage': 'English',
        },
      ],
      'areaServed': {
        '@type': 'Place',
        'name': 'North America',
      },
    }}
    id="sd-enhanced-organization"
  />
)
